import { useEffect, useMemo, useState } from 'react'
import { api, fmt } from '../lib/api.js'
import { Chip, Empty, ErrorNote, Kpi } from '../components/ui.jsx'
import { ROLE_FILTERS } from '../lib/roles.js'

// Campaigns: the plan and the state. Never the engine.
//
// Nothing here sends anything or detects replies. A campaign holds who is in
// it, why, which step they are on and what came back, and works out what is
// due next. A person presses send from their own mail client and logs what
// happened. The interface says so rather than implying a mailbox connection.

const OUTCOME_TONE = { positive: 'named', referred: 'replied', not_now: 'awaiting', no: 'stale', no_reply: 'plain' }
const STATUS_TONE = { active: 'awaiting', meeting: 'named', referred: 'replied', snoozed: 'awaiting', closed: 'stale', exhausted: 'plain' }
const statusChip = (s) => (s === 'active' ? 'named' : s === 'closed' ? 'stale' : 'plain')

// ---------------------------------------------------------- step 2: people
//
// Three filters, because those are the three decisions: which problem you are
// selling against (focus area), how strongly the company carries it (fit), and
// who inside the company you want (title). Companies fall out of that, contacts
// fall out of the companies, and the segment -- which decides what the opening
// email says -- is worked out from the account rather than chosen.

const FOCUS_AREAS = [
  { key: 'wc', label: 'Working capital', fitKey: 'wc_fit' },
  { key: 'proc', label: 'Procurement', fitKey: 'proc_fit' },
]
const FIT_BANDS = ['Strong', 'Possible', 'Weak', 'Below gate', 'Unrated']
const bandOf = (fit) => (fit ? fit.fit || 'Unrated' : 'Unrated')

function PickPeople({ draft, accounts, onBack, onCancel, onCreated }) {
  const [area, setArea] = useState('wc')
  const [bands, setBands] = useState(['Strong'])
  const [roles, setRoles] = useState(['finance', 'operations'])
  const [dropped, setDropped] = useState(() => new Set())
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  const focus = FOCUS_AREAS.find((f) => f.key === area)
  const patterns = roles.map((k) => ROLE_FILTERS.find((r) => r.key === k)?.re).filter(Boolean)

  // Companies that pass focus area + fit, then the people inside them that pass
  // the title filter. An account with nobody matching is still listed, greyed
  // out, rather than silently missing -- "why is X not here" is the question
  // this screen has to answer without being asked.
  const matches = useMemo(
    () =>
      accounts
        .filter((a) => bands.includes(bandOf(a[focus.fitKey])))
        .map((a) => ({
          slug: a.slug,
          name: a.name,
          band: bandOf(a[focus.fitKey]),
          people: (a.contacts || []).filter(
            (c) => !c.is_placeholder && (!patterns.length || patterns.some((re) => re.test(c.title || ''))),
          ),
        }))
        .sort((x, y) => y.people.length - x.people.length || x.name.localeCompare(y.name)),
    [accounts, area, bands.join(), roles.join()],
  )

  // Companies with nobody in those roles are still selected by default: they
  // join the campaign as a gap to fill rather than being quietly left out.
  const chosen = matches.filter((m) => !dropped.has(m.slug))
  const people = chosen.reduce((n, m) => n + m.people.length, 0)
  const gaps = chosen.filter((m) => !m.people.length).length
  const bandCount = (b) => accounts.filter((a) => bandOf(a[focus.fitKey]) === b).length

  const create = async () => {
    setBusy(true)
    setErr(null)
    try {
      const out = await api.buildCampaign({
        name: draft.name.trim(),
        description: draft.description || '',
        discipline: area,
        slugs: chosen.map((m) => m.slug),
        roles: patterns.map((re) => re.source),
      })
      onCreated(out.id)
    } catch (e) {
      setErr(e)
      setBusy(false)
    }
  }

  return (
    <>
      <div className="topbar">
        <h1>{draft.name}</h1>
        <span className="topbar-note">
          Step 2 of 2 · {chosen.length} companies · {people} people
          {gaps ? ` · ${gaps} with nobody to email yet` : ''}
        </span>
        <div className="topbar-right">
          <button className="btn" onClick={onBack}>Back</button>
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button className="btn primary" onClick={create} disabled={busy || !chosen.length}>
            {busy ? 'Creating…' : `Create with ${chosen.length}`}
          </button>
        </div>
      </div>

      <div className="body" style={{ display: 'flex', flexDirection: 'column', gap: 8, minHeight: 0 }}>
        <div className="card">
          <div className="row" style={{ gap: 6, padding: '8px 12px', flexWrap: 'wrap' }}>
            <span className="lbl" style={{ alignSelf: 'center', width: 74 }}>Focus area</span>
            {FOCUS_AREAS.map((f) => (
              <button key={f.key} className={`btn ${area === f.key ? 'on' : ''}`}
                onClick={() => setArea(f.key)}>{f.label}</button>
            ))}
            <span className="card-sub" style={{ marginLeft: 8 }}>
              Commercial excellence, plant scheduling and cost have no fit model yet
            </span>
          </div>
          <div className="row" style={{ gap: 6, padding: '0 12px 8px', flexWrap: 'wrap' }}>
            <span className="lbl" style={{ alignSelf: 'center', width: 74 }}>Fit</span>
            {FIT_BANDS.filter((b) => bandCount(b) || bands.includes(b)).map((b) => (
              <button key={b} className={`btn ${bands.includes(b) ? 'on' : ''}`}
                onClick={() => setBands((v) => (v.includes(b) ? v.filter((x) => x !== b) : [...v, b]))}>
                {b}<span className="mono" style={{ marginLeft: 4, color: 'var(--tertiary)' }}>{bandCount(b)}</span>
              </button>
            ))}
          </div>
          <div className="row" style={{ gap: 6, padding: '0 12px 9px', flexWrap: 'wrap' }}>
            <span className="lbl" style={{ alignSelf: 'center', width: 74 }}>Title</span>
            {ROLE_FILTERS.map((r) => (
              <button key={r.key} className={`btn ${roles.includes(r.key) ? 'on' : ''}`}
                onClick={() => setRoles((v) => (v.includes(r.key) ? v.filter((x) => x !== r.key) : [...v, r.key]))}>
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {err ? <ErrorNote error={err} /> : null}

        <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div className="card-header">
            <span className="card-title">Who this brings in</span>
            <span className="card-sub" style={{ marginLeft: 'auto' }}>
              Untick a company to leave it out. Companies with nobody to email still join,
              so the gap stays visible.
            </span>
          </div>
          <div className="tbl-wrap">
            {matches.length === 0 ? (
              <Empty>Nothing matches that combination.</Empty>
            ) : (
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ width: 34 }} />
                    <th>Company</th>
                    <th className="mid" style={{ width: 90 }}>Fit</th>
                    <th className="num" style={{ width: 62 }}>People</th>
                    <th>Who</th>
                  </tr>
                </thead>
                <tbody>
                  {matches.map((m) => {
                    const out = dropped.has(m.slug)
                    return (
                      <tr key={m.slug} style={{ opacity: out ? 0.45 : 1, cursor: 'default' }}>
                        <td>
                          <input type="checkbox" checked={!out}
                            onChange={() => setDropped((v) => {
                              const n = new Set(v)
                              if (n.has(m.slug)) n.delete(m.slug)
                              else n.add(m.slug)
                              return n
                            })} />
                        </td>
                        <td style={{ fontWeight: 500 }}>{m.name}</td>
                        <td className="mid">
                          <Chip kind={m.band === 'Strong' ? 'named' : m.band === 'Possible' ? 'mentioned' : 'plain'}>
                            {m.band}
                          </Chip>
                        </td>
                        <td className="num mono">{m.people.length || '—'}</td>
                        <td style={{ color: 'var(--muted)', whiteSpace: 'normal' }}>
                          {!m.people.length ? (
                            <span style={{ color: 'var(--neg)' }}>nobody in those roles — joins as a gap</span>
                          ) : (
                            <span title={m.people.map((c) => `${c.name} - ${c.title || ''}`).join('\n')}>
                              {m.people.slice(0, 3).map((c) => c.name).join(', ')}
                              {m.people.length > 3 ? ` and ${m.people.length - 3} more` : ''}
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

// ---------------------------------------------------------------- the list

export default function Campaigns({ campaigns, due, accounts = [], onOpen, onReload }) {
  // Which row is asking to be confirmed. One at a time, in the row itself, so
  // the thing you are about to delete is still on screen next to the button.
  const [confirming, setConfirming] = useState(null)
  const [creating, setCreating] = useState(null)
  const [error, setError] = useState(null)

  if (creating) {
    return creating.step === 2 ? (
      <PickPeople
        draft={creating}
        accounts={accounts}
        onBack={() => setCreating({ ...creating, step: 1 })}
        onCancel={() => setCreating(null)}
        onCreated={async (id) => { setCreating(null); await onReload(); onOpen(id) }}
      />
    ) : (
      <>
        <div className="topbar">
          <h1>New campaign</h1>
          <span className="topbar-note">Step 1 of 2 · what this campaign is</span>
        </div>
        <div className="body">
          <div className="card" style={{ maxWidth: 620 }}>
            <div className="card-body stack">
              <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--ink-soft)' }}>
                A campaign is one set of people worked through one sequence. It does not send anything:
                you send from your own mail client and log what happened. The opening email varies by
                segment, which you will see on the next step.
              </div>
              <ErrorNote error={error} />
              <label className="stack" style={{ gap: 3 }}>
                <span className="lbl">Name</span>
                <input className="input" placeholder="Week 1, working capital" value={creating.name || ''}
                  onChange={(e) => setCreating({ ...creating, name: e.target.value })} autoFocus />
              </label>
              <label className="stack" style={{ gap: 3 }}>
                <span className="lbl">Description</span>
                <textarea className="input" placeholder="Who this is for and why now"
                  value={creating.description || ''}
                  onChange={(e) => setCreating({ ...creating, description: e.target.value })} />
              </label>
              <label className="stack" style={{ gap: 3, maxWidth: 200 }}>
                <span className="lbl">Launch date</span>
                <input className="input" type="date" value={creating.launch_date || ''}
                  onChange={(e) => setCreating({ ...creating, launch_date: e.target.value })} />
              </label>
              <div className="row">
                <button className="btn primary" disabled={!(creating.name || '').trim()}
                  onClick={() => setCreating({ ...creating, step: 2 })}>
                  Next: add people
                </button>
                <button className="btn" onClick={() => setCreating(null)}>Cancel</button>
                <span className="card-sub" style={{ marginLeft: 'auto' }}>
                  Default steps: email, LinkedIn at +3d, email at +7d
                </span>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="topbar">
        <h1>Campaigns</h1>
        <span className="topbar-note">
          {campaigns.length} {campaigns.length === 1 ? 'campaign' : 'campaigns'}
          {due?.length ? ` · ${due.length} due` : ''}
        </span>
        <div className="topbar-right">
          <button className="btn primary" onClick={() => setCreating({ name: '' })}>+ New campaign</button>
        </div>
      </div>

      <div className="body">
        {campaigns.length === 0 ? (
          <Empty>No campaigns yet. Start one and add people from your researched accounts.</Empty>
        ) : (
          <div className="card">
            <div className="tbl-wrap" style={{ maxHeight: 'none' }}>
              <table className="tbl">
                <thead>
                  <tr>
                    <th style={{ minWidth: 240 }}>Campaign</th>
                    <th style={{ width: 150 }}>Angle</th>
                    <th style={{ width: 90 }}>Status</th>
                    <th style={{ width: 90 }}>Launch</th>
                    <th className="num" style={{ width: 70 }}>People</th>
                    <th className="num" style={{ width: 60 }}>Due</th>
                    <th className="num" style={{ width: 80 }}>Meetings</th>
                    <th className="num" style={{ width: 80 }}>Snoozed</th>
                    <th className="num" style={{ width: 70 }}>Closed</th>
                    <th style={{ width: 120 }} />
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((c) => (
                    <tr key={c.id} onClick={() => onOpen(c.id)}>
                      <td style={{ whiteSpace: 'normal' }}>
                        <div style={{ fontWeight: 500 }}>{c.name}</div>
                        {c.description ? <div className="card-sub">{c.description}</div> : null}
                      </td>
                      <td>{c.angle ? <Chip kind="replied">{c.angle.replace(/_/g, ' ').toUpperCase()}</Chip> : <span className="chip absent">—</span>}</td>
                      <td><Chip kind={statusChip(c.status)}>{c.status.toUpperCase()}</Chip></td>
                      <td className="mono" style={{ color: 'var(--muted)' }}>{fmt.date(c.launch_date)}</td>
                      <td className="num">{c.member_count}</td>
                      <td className="num" style={c.stats?.due ? { color: 'var(--neg)', fontWeight: 600 } : { color: 'var(--tertiary)' }}>{c.stats?.due || 0}</td>
                      <td className="num" style={c.stats?.meeting ? { color: 'var(--accent-ink)', fontWeight: 600 } : undefined}>{c.stats?.meeting || 0}</td>
                      <td className="num">{c.stats?.snoozed || 0}</td>
                      <td className="num" style={{ color: 'var(--tertiary)' }}>{c.stats?.closed || 0}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {confirming === c.id ? (
                          <span className="row" style={{ gap: 4 }}>
                            <button className="btn" style={{ borderColor: 'var(--neg)', color: 'var(--neg)' }}
                              onClick={async () => {
                                await api.deleteCampaign(c.id)
                                setConfirming(null)
                                await onReload()
                              }}>
                              Delete {c.member_count || 0}
                            </button>
                            <button className="btn" onClick={() => setConfirming(null)}>Cancel</button>
                          </span>
                        ) : (
                          <button className="btn link" onClick={() => setConfirming(c.id)}>Delete</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="row" style={{ padding: '8px 12px', borderTop: '0.5px solid var(--line)' }}>
              <span className="card-sub">Click a campaign to open it. Nothing sends from this app.</span>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

// -------------------------------------------------------------- the detail

// The opening for one segment, shown when that segment is selected above the
// list. Kept next to the people it is addressed to rather than in a panel of
// its own: reading the email and seeing who gets it is one action.
function Opening({ board, group, onSaved }) {
  const v = board.variants?.[group.segment]
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({ subject: '', body: '' })
  const [busy, setBusy] = useState(false)

  if (!v) {
    return (
      <div className="card" style={{ marginBottom: 8 }}>
        <div className="card-header">
          <span className="card-title">{group.label}</span>
          <span className="card-sub" style={{ marginLeft: 'auto' }}>
            These people carry no segment, so there is no opening to start from. Write it by hand.
          </span>
        </div>
      </div>
    )
  }

  const save = async () => {
    setBusy(true)
    try {
      await api.setVariant(board.id, group.segment, draft)
      setEditing(false)
      await onSaved()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card" style={{ marginBottom: 8 }}>
      <div className="card-header" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="card-title">{group.label}</span>
        {v.edited ? <Chip kind="plain">edited</Chip> : null}
        <span className="card-sub">{v.observation}</span>
        {!editing ? (
          <button className="btn" style={{ marginLeft: 'auto' }}
            onClick={() => { setDraft({ subject: v.subject || '', body: v.body || '' }); setEditing(true) }}>
            Edit opening
          </button>
        ) : null}
      </div>

      <div style={{ padding: '9px 12px' }}>
        {!editing ? (
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 420px', minWidth: 320, border: '0.5px solid var(--line)',
              background: 'var(--bg)', padding: '8px 10px' }}>
              <div style={{ fontWeight: 600, marginBottom: 5 }}>{v.subject}</div>
              <div style={{ fontSize: 'var(--fs-pill)', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{v.body}</div>
            </div>
            {v.bullets?.length ? (
              <div style={{ flex: '1 1 300px', minWidth: 260 }}>
                <div className="lbl" style={{ marginBottom: 4 }}>Points to address</div>
                <ol style={{ margin: 0, paddingLeft: 17 }}>
                  {v.bullets.map((b, i) => (
                    <li key={i} style={{ fontSize: 'var(--fs-pill)', lineHeight: 1.45 }}>{b}</li>
                  ))}
                </ol>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="stack">
            <input className="input" value={draft.subject} placeholder="Subject"
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })} />
            <textarea className="input" rows={12} value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })} />
            <div className="row">
              <button className="btn primary" onClick={save} disabled={busy}>Save opening</button>
              <button className="btn" onClick={() => setEditing(false)}>Cancel</button>
              <span className="card-sub" style={{ marginLeft: 8 }}>
                Every figure has to come from that company's own disclosure.
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export function CampaignDetail({ campaignId, accounts, outcomes, onClose, onOpenAccount, onChanged }) {
  const [board, setBoard] = useState(null)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const [adding, setAdding] = useState(false)
  const [pickedAccounts, setPickedAccounts] = useState(() => new Set())
  const [pickedRoles, setPickedRoles] = useState(() => new Set(['finance', 'procurement']))
  const [addResult, setAddResult] = useState(null)
  const [logging, setLogging] = useState(null)
  const [note, setNote] = useState('')
  const [steps, setSteps] = useState(null)
  // Which segment's opening is on screen. null means show everyone, which
  // is what you want when you are working the list rather than the copy.
  const [segment, setSegment] = useState(null)
  // Two presses, not a browser confirm(): the second press states what is
  // about to be lost, which a native dialog cannot do well.
  const [confirmDelete, setConfirmDelete] = useState(false)

  const load = () => api.campaign(campaignId).then(setBoard).catch(setError)
  useEffect(() => {
    setBoard(null)
    load()
  }, [campaignId])

  const candidates = useMemo(() => {
    const res = ROLE_FILTERS.filter((r) => pickedRoles.has(r.key))
    const out = []
    for (const slug of pickedAccounts) {
      const a = accounts.find((x) => x.slug === slug)
      if (!a) continue
      for (const c of a.contacts || []) {
        if (c.is_placeholder) continue
        if (res.length && !res.some((r) => r.re.test(c.title || ''))) continue
        out.push({ account_slug: slug, contact_name: c.name })
      }
    }
    return out
  }, [pickedAccounts, pickedRoles, accounts])

  const run = async (fn) => {
    setBusy(true)
    try {
      await fn()
      await load()
      onChanged?.()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const addMembers = () =>
    run(async () => {
      setAddResult(await api.addMembers(campaignId, candidates))
      setPickedAccounts(new Set())
    })

  const logOutcome = (m, outcome) =>
    run(async () => {
      await api.logOutcome(campaignId, m.key, { outcome, note: note.trim() })
      setLogging(null)
      setNote('')
    })

  const saveSteps = () =>
    run(async () => {
      await api.patchCampaign(campaignId, { steps })
      setSteps(null)
    })

  const setStatus = (status) => run(() => api.patchCampaign(campaignId, { status }))

  if (error) return <div className="body"><ErrorNote error={error} /></div>
  if (!board) return <div className="body"><Empty>Loading…</Empty></div>

  // One table, rendered once per segment. Kept as a function rather than a
  // component so the log-outcome state above stays in one place: an inner
  // component would remount on every keystroke in the note field.
  const shown = segment
    ? board.members.filter((m) => (m.segment || 'unsegmented') === segment)
    : board.members
  const chosenGroup = board.segments.find((g) => g.segment === segment)

  const memberTable = (list) => (
    <div className="tbl-wrap" style={{ maxHeight: 'none' }}>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 116, minWidth: 116 }}>Company</th>
            <th style={{ width: 110, minWidth: 110 }}>Contact</th>
            <th style={{ minWidth: 300 }}>Title</th>
            <th style={{ width: 150, minWidth: 150 }}>Opening</th>
            <th style={{ minWidth: 180 }}>Next action</th>
            <th style={{ width: 70 }}>Due</th>
            <th style={{ width: 80 }}>Status</th>
            <th style={{ width: 95 }}>Outcome</th>
            <th style={{ width: 90 }}>Log</th>
          </tr>
        </thead>
        <tbody>
          {list.flatMap((m) => [
            <tr key={m.key} style={{ cursor: 'default' }}>
              {/* The name has to be allowed to wrap, or a long one
                  ("International Flavors & Fragrances Inc.") widens
                  the column regardless of what the header asks for. */}
              <td style={{ whiteSpace: 'normal', maxWidth: 116 }}>
                <button className="btn link" style={{ textAlign: 'left', whiteSpace: 'normal' }}
                  onClick={() => onOpenAccount(m.account_slug)}>
                  {m.account_name}
                </button>
              </td>
              <td style={{ fontWeight: 500, whiteSpace: 'normal' }}>
                {m.contact_name || <span style={{ color: 'var(--neg)' }}>Nobody yet</span>}
              </td>
              <td style={{ color: 'var(--muted)', whiteSpace: 'normal' }}>
                {m.contact_name ? m.contact?.title : 'no contact on file yet'}
              </td>
              <td style={{ whiteSpace: 'normal' }}>
                {/* Which opening this person gets. Clickable, because "what
                    are we sending them" is the question you ask while
                    looking at the row. */}
                <button className="btn link" style={{ textAlign: 'left' }}
                  onClick={() => setSegment(m.segment || 'unsegmented')}>
                  {board.segments.find((g) => g.segment === (m.segment || 'unsegmented'))?.label
                    || 'No segment'}
                </button>
              </td>
              <td>
                {m.next ? (
                  <span>
                    <Chip kind={m.next.channel === 'linkedin' ? 'sequenced' : 'plain'}>{m.next.channel.toUpperCase()}</Chip>{' '}
                    <span className="mono" style={{ color: 'var(--tertiary)' }}>{m.next.step}</span> {m.next.label}
                  </span>
                ) : (
                  <span style={{ color: 'var(--tertiary)' }}>nothing scheduled</span>
                )}
              </td>
              <td className="mono" style={m.next?.overdue ? { color: 'var(--neg)', fontWeight: 600 } : { color: 'var(--muted)' }}>
                {m.next ? fmt.date(m.next.due_on) : '—'}
              </td>
              <td><Chip kind={STATUS_TONE[m.status] || 'plain'}>{fmt.status(m.status)}</Chip></td>
              <td>
                {m.last_outcome ? (
                  <Chip kind={OUTCOME_TONE[m.last_outcome]}>{outcomes?.[m.last_outcome]?.label?.toUpperCase() || m.last_outcome}</Chip>
                ) : (<span className="chip absent">—</span>)}
              </td>
              <td>
                {m.status === 'active' ? (
                  <button className="btn link" onClick={() => { setLogging(logging === m.key ? null : m.key); setNote('') }}>
                    {logging === m.key ? 'Close' : 'Log'}
                  </button>
                ) : (
                  <span className="card-sub">{m.status === 'snoozed' && m.due_on ? `wakes ${fmt.date(m.due_on)}` : ''}</span>
                )}
              </td>
            </tr>,
            logging === m.key ? (
              <tr key={`${m.key}-log`} style={{ background: 'var(--bg)' }}>
                <td colSpan={9} style={{ whiteSpace: 'normal' }}>
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <span className="lbl">Step {m.next?.step ?? m.step} · {m.next?.channel || 'email'} · {m.contact_name}</span>
                    <input className="input" style={{ flex: 1, minWidth: 240 }} placeholder="What happened. This becomes a touch on the account." value={note} onChange={(e) => setNote(e.target.value)} autoFocus />
                    {Object.entries(outcomes || {}).map(([k, o]) => (
                      <button
                        key={k}
                        className={`btn ${k === 'positive' ? 'primary' : ''}`}
                        onClick={() => logOutcome(m, k)}
                        disabled={busy}
                        title={k === 'no_reply' ? 'Moves to the next step' : k === 'not_now' ? 'Snoozes 90 days' : 'Ends the sequence'}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                </td>
              </tr>
            ) : null,
          ])}
        </tbody>
      </table>
    </div>
  )

  return (
    <>
      <div className="slide-head">
        <button className="slide-close" onClick={onClose} title="Close (Esc)" aria-label="Close">&times;</button>
        <h1>{board.name}</h1>
        <Chip kind={statusChip(board.status)}>{board.status.toUpperCase()}</Chip>
        {board.angle ? <Chip kind="replied">{board.angle.replace(/_/g, ' ').toUpperCase()}</Chip> : null}
        <span className="topbar-note">launch {fmt.date(board.launch_date)}</span>
        <div className="topbar-right">
          <button className={`btn ${adding ? 'on' : ''}`} onClick={() => setAdding((v) => !v)}>Add people</button>
          {board.status === 'draft' ? <button className="btn primary" onClick={() => setStatus('active')}>Launch</button> : null}
          {board.status === 'active' ? <button className="btn" onClick={() => setStatus('closed')}>Close</button> : null}
          {!confirmDelete ? (
            <button className="btn" onClick={() => setConfirmDelete(true)}>Delete</button>
          ) : (
            <>
              <button className="btn" style={{ borderColor: 'var(--neg)', color: 'var(--neg)' }}
                disabled={busy}
                onClick={async () => {
                  setBusy(true)
                  try {
                    await api.deleteCampaign(campaignId)
                    await onChanged()
                    onClose()
                  } finally {
                    setBusy(false)
                  }
                }}>
                Delete {board.name} and {board.members.length}{' '}
                {board.members.length === 1 ? 'person' : 'people'}
              </button>
              <button className="btn" onClick={() => setConfirmDelete(false)}>Cancel</button>
            </>
          )}
        </div>
      </div>

      <div className="body" style={{ overflow: 'auto' }}>
        <div className="col">
          <ErrorNote error={error} />
          {board.description ? <div className="card-sub" style={{ marginBottom: 10 }}>{board.description}</div> : null}

          <div className="kpi-strip">
            <Kpi label="People" value={board.stats.members} sub={`${board.stats.active} active`} />
            <Kpi label="Due now" value={board.stats.due} tone={board.stats.due ? 'warn' : 'nd'} sub="step overdue" />
            <Kpi label="Meetings" value={board.stats.meeting} tone="pos" />
            <Kpi label="Snoozed" value={board.stats.snoozed} sub="not now" />
            <Kpi label="Closed" value={board.stats.closed} tone="nd" />
            {board.stats.no_contact ? (
              <Kpi label="No contact" value={board.stats.no_contact}
                sub="companies to find people for" tone="warn" />
            ) : null}
          </div>

          <div className="card" style={{ marginBottom: 12 }}>
            <div className="card-header">
              <span className="card-title">Steps</span>
              <button className="btn link" onClick={() => setSteps(steps ? null : board.steps.map((s) => ({ ...s })))}>
                {steps ? 'Cancel' : 'Edit'}
              </button>
            </div>
            <div className="card-body">
              {!steps ? (
                <div className="row" style={{ gap: 16, flexWrap: 'wrap' }}>
                  {board.steps.map((s) => (
                    <span key={s.n} style={{ fontSize: 'var(--fs-pill)' }}>
                      <span className="mono" style={{ color: 'var(--accent-ink)', fontWeight: 600 }}>{s.n}</span>{' '}
                      <Chip kind={s.channel === 'linkedin' ? 'sequenced' : 'plain'}>{s.channel.toUpperCase()}</Chip>{' '}
                      {s.label} <span className="card-sub">+{s.wait_days}d</span>
                    </span>
                  ))}
                </div>
              ) : (
                <div className="stack">
                  {board.stats.in_flight ? (
                    <div className="notice" style={{ display: 'block' }}>
                      <div className="notice-title">
                        {board.stats.in_flight} {board.stats.in_flight === 1 ? 'person is' : 'people are'} already mid-sequence
                      </div>
                      <div className="notice-body" style={{ marginTop: 3 }}>
                        They have reached step {board.stats.max_step_reached}. Removing a step they have passed leaves
                        them with nothing scheduled.
                      </div>
                    </div>
                  ) : null}

                  {steps.map((st, i) => (
                    <div className="row" key={i} style={{ gap: 8 }}>
                      <span className="mono" style={{ width: 18, color: 'var(--tertiary)' }}>{i + 1}</span>
                      <select className="input" style={{ width: 110 }} value={st.channel} onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, channel: e.target.value } : x)))}>
                        <option value="email">Email</option>
                        <option value="linkedin">LinkedIn</option>
                      </select>
                      <input className="input" style={{ flex: 1 }} placeholder="What this touch is for" value={st.label} onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
                      <label className="row" style={{ gap: 4 }}>
                        <span className="card-sub">wait</span>
                        <input className="input mono" type="number" min="0" max="120" style={{ width: 62 }} value={st.wait_days} onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, wait_days: e.target.value } : x)))} />
                        <span className="card-sub">days</span>
                      </label>
                      <button className="btn" disabled={steps.length < 2} title={steps.length < 2 ? 'A campaign needs at least one step' : 'Remove this step'} onClick={() => setSteps(steps.filter((_, j) => j !== i))}>&times;</button>
                    </div>
                  ))}

                  <div className="row">
                    <button className="btn" onClick={() => setSteps([...steps, { n: steps.length + 1, channel: 'email', label: '', wait_days: 7 }])}>+ Add step</button>
                    <span className="card-sub">Wait days count from the previous action. LinkedIn steps skip anyone without a profile URL.</span>
                    <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={saveSteps} disabled={busy}>Save steps</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {adding ? (
            <div className="card" style={{ marginBottom: 12 }}>
              <div className="card-header">
                <span className="card-title">Add people</span>
                <span className="card-sub">pick companies, then the roles you want from each</span>
              </div>
              <div className="card-body stack">
                <div>
                  <div className="lbl" style={{ marginBottom: 6 }}>Roles</div>
                  <div className="row" style={{ flexWrap: 'wrap' }}>
                    {ROLE_FILTERS.map((r) => (
                      <button
                        key={r.key}
                        className={`btn ${pickedRoles.has(r.key) ? 'on' : ''}`}
                        onClick={() => setPickedRoles((s) => { const n = new Set(s); n.has(r.key) ? n.delete(r.key) : n.add(r.key); return n })}
                      >
                        {r.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="lbl" style={{ marginBottom: 6 }}>Companies, high fit first</div>
                  <div className="row" style={{ flexWrap: 'wrap', maxHeight: 130, overflowY: 'auto' }}>
                    {accounts.filter((a) => a.contact_count > 0).slice(0, 60).map((a) => (
                      <button
                        key={a.slug}
                        className={`btn ${pickedAccounts.has(a.slug) ? 'on' : ''}`}
                        onClick={() => setPickedAccounts((s) => { const n = new Set(s); n.has(a.slug) ? n.delete(a.slug) : n.add(a.slug); return n })}
                      >
                        {a.name}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="row">
                  <span className="card-sub">{pickedAccounts.size} companies · <b>{candidates.length} people</b> match those roles</span>
                  <button className="btn primary" style={{ marginLeft: 'auto' }} onClick={addMembers} disabled={busy || !candidates.length}>
                    Add {candidates.length || ''}
                  </button>
                </div>
                {addResult ? (
                  <div className="notice" style={{ display: 'block' }}>
                    <div className="notice-title">Added {addResult.added}</div>
                    {addResult.rejected.length ? (
                      <div className="notice-body" style={{ marginTop: 4 }}>
                        {addResult.rejected.length} not added:
                        {addResult.rejected.slice(0, 6).map((r, i) => (<div key={i}>· {r.contact_name} — {r.reason}</div>))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}

          {chosenGroup ? <Opening board={board} group={chosenGroup} onSaved={load} /> : null}

          <div className="card">
            <div className="card-header" style={{ gap: 6, flexWrap: 'wrap' }}>
              <span className="card-title">Who is in this campaign</span>
              <span className="card-sub" style={{ marginLeft: 8 }}>
                nothing sends from here · log what you did and the next step appears
              </span>
            </div>

            {/* The openings, as a filter. Pick one to read the email and see
                only the people it is going to. */}
            <div className="row" style={{ gap: 6, padding: '8px 12px', flexWrap: 'wrap',
              borderBottom: '0.5px solid var(--line)' }}>
              <span className="lbl" style={{ alignSelf: 'center' }}>Openings</span>
              <button className={`btn ${segment === null ? 'on' : ''}`} onClick={() => setSegment(null)}>
                Everyone
                <span className="mono" style={{ marginLeft: 4, color: 'var(--tertiary)' }}>
                  {board.members.length}
                </span>
              </button>
              {board.segments.map((g) => (
                <button key={g.segment} className={`btn ${segment === g.segment ? 'on' : ''}`}
                  onClick={() => setSegment(segment === g.segment ? null : g.segment)}
                  title={board.variants?.[g.segment]?.observation || ''}>
                  {g.label}
                  <span className="mono" style={{ marginLeft: 4, color: 'var(--tertiary)' }}>
                    {g.members}{g.reply_rate != null ? ` · ${g.reply_rate}%` : ''}
                  </span>
                </button>
              ))}
            </div>

            {shown.length === 0 ? (
              <Empty>Nobody in this campaign yet.</Empty>
            ) : (
              memberTable(shown)
            )}
          </div>
        </div>
      </div>
    </>
  )
}
